import { adTypes } from '../../../configs';
import {
  AD_SELECTOR_POD_SLOT_MISMATCH_ERROR,
  AD_SELECTOR_PREROLL_REQUIREMENTS_ERROR,
  AD_SELECTOR_SLATE_AD_SELECTOR_VIDEO_AMOUNT_AD_SEQUENCE_ERROR,
  AD_SELECTOR_SLATE_ORDER_ERROR,
  AD_SELECTOR_SLATE_PREROLL_START_ERROR,
  AD_TYPE_CONFLICT_AD_SEQUENCE_ERROR,
  CONFLICTING_PARENT_LINE_OMS_LINKS_AD_SEQUENCE_ERROR,
  EMPTY_AD_SEQUENCE_ERROR,
  POD_POSITION_UNSET_ERROR,
  PREROLL_REQUIREMENTS_ERROR,
  REORDER_ACROSS_POD_ERROR,
  SLATE_VIDEO_AMOUNT_AD_SEQUENCE_ERROR,
  SLOT_POSITION_UNSET_ERROR,
} from '../constants';
import { getDefaultSlotPosition } from '../slotPositionConstants';
import type { SequenceAdV5, TransformedSequence } from '../types';

export type RowError = {
  adId: string;
  message: string;
};

export type ValidationResult = {
  errorsWithSequenceIds: Record<string, Set<string>>;
  rowErrors: RowError[];
};

export const getDefaultErrorsState = (): Record<string, Set<string>> => {
  return {
    [AD_TYPE_CONFLICT_AD_SEQUENCE_ERROR]: new Set(),
    [SLATE_VIDEO_AMOUNT_AD_SEQUENCE_ERROR]: new Set(),
    [AD_SELECTOR_SLATE_AD_SELECTOR_VIDEO_AMOUNT_AD_SEQUENCE_ERROR]: new Set(),
    [EMPTY_AD_SEQUENCE_ERROR]: new Set(),
    [CONFLICTING_PARENT_LINE_OMS_LINKS_AD_SEQUENCE_ERROR]: new Set(),
    [PREROLL_REQUIREMENTS_ERROR]: new Set(),
    [AD_SELECTOR_PREROLL_REQUIREMENTS_ERROR]: new Set(),
    [REORDER_ACROSS_POD_ERROR]: new Set(),
    [AD_SELECTOR_POD_SLOT_MISMATCH_ERROR]: new Set(),
    [AD_SELECTOR_SLATE_ORDER_ERROR]: new Set(),
    [AD_SELECTOR_SLATE_PREROLL_START_ERROR]: new Set(),
  };
};

// property name is a type of an ad to validate
// min shows, if quantity number should be minimum or exact amount of ads
export type AdSequenceValidationRule = Record<
  string,
  {
    min: boolean;
    quantity: number;
  }
>;

// property name is an error, that will be shown
export const AD_SEQUENCE_VALIDATION_RULES: Record<string, AdSequenceValidationRule> = {
  [SLATE_VIDEO_AMOUNT_AD_SEQUENCE_ERROR]: {
    [adTypes.SLATE.key]: {
      min: true,
      quantity: 1,
    },
    [adTypes.VIDEO.key]: {
      min: true,
      quantity: 1,
    },
  },
  [AD_SELECTOR_SLATE_AD_SELECTOR_VIDEO_AMOUNT_AD_SEQUENCE_ERROR]: {
    [adTypes.AD_SELECTOR_SLATE.key]: {
      min: true,
      quantity: 1,
    },
    [adTypes.AD_SELECTOR_VIDEO.key]: {
      min: true,
      quantity: 1,
    },
  },
};

// Relaxed rules for standalone lines: slate-only sequences (no video required)
const STANDALONE_SLATE_ONLY_VALIDATION_RULES: Record<string, AdSequenceValidationRule> = {
  [SLATE_VIDEO_AMOUNT_AD_SEQUENCE_ERROR]: {
    [adTypes.SLATE.key]: {
      min: true,
      quantity: 1,
    },
  },
  [AD_SELECTOR_SLATE_AD_SELECTOR_VIDEO_AMOUNT_AD_SEQUENCE_ERROR]: {
    [adTypes.AD_SELECTOR_SLATE.key]: {
      min: true,
      quantity: 1,
    },
  },
};

export const isStandaloneLineSequence = (ads: SequenceAdV5[]): boolean =>
  ads.every((ad) => !ad.lineItem?.parentLineOmsLink?.id);

export const getAdSequenceValidationRule = (
  validationKey: string,
  ads: SequenceAdV5[],
  allowStandaloneLines: boolean
): AdSequenceValidationRule => {
  if (allowStandaloneLines && isStandaloneLineSequence(ads)) {
    return STANDALONE_SLATE_ONLY_VALIDATION_RULES[validationKey];
  }

  return AD_SEQUENCE_VALIDATION_RULES[validationKey];
};

// get rules key to use according to SLATE/VIDEO or AD_SELECTOR_SLATE/AD_SELECTOR_VIDEO combinations
export const getAdSequenceValidationKey = (type: string): string | null => {
  return (
    Object.keys(AD_SEQUENCE_VALIDATION_RULES).find((key) => {
      return Object.keys(AD_SEQUENCE_VALIDATION_RULES[key]).includes(type);
    }) || null
  );
};

// check, if ad quantities meets rule
export const validateAdsQuantities = (
  rule: AdSequenceValidationRule,
  adQuantities: Record<string, number>
): boolean => {
  let isValid = true;

  // check, if ad sequence to contain all ad types with need amount of quantity
  Object.entries(rule).forEach((entries) => {
    const [key, value] = entries;
    const adQuantity = adQuantities[key];

    if (
      !adQuantity ||
      (adQuantity && value.quantity > adQuantity) ||
      (adQuantity && !value.min && value.quantity !== adQuantity)
    ) {
      isValid = false;
    }
  });

  return isValid;
};

export type CalculateAdQuantitiesOptions = {
  allowStandaloneLines?: boolean;
};

// calculate ad quantities and check, if there are ads out of the combination
export const calculateAdQuantities = (
  rule: AdSequenceValidationRule,
  ads: SequenceAdV5[],
  { allowStandaloneLines = false }: CalculateAdQuantitiesOptions = {}
): { errors: string[]; adQuantities: Record<string, number> } => {
  const errors: Set<string> = new Set();
  const uniqueParentLineOmsLink: Set<string> = new Set();
  const initialQuantities: Record<string, number> = {};

  // calculate amount of appearances by each type in the ad sequence
  const adQuantities = ads.reduce((acc, current) => {
    // check, if ad sequence has ads that are not in the combination
    if (rule[current.type]) {
      acc[current.type] = acc[current.type] ? ++acc[current.type] : 1;
      if (current.lineItem?.parentLineOmsLink?.id) uniqueParentLineOmsLink.add(current.lineItem?.parentLineOmsLink?.id);
    } else {
      errors.add(AD_TYPE_CONFLICT_AD_SEQUENCE_ERROR);
    }

    return acc;
  }, initialQuantities);

  // check, if all ads have the same parent line oms link
  if (uniqueParentLineOmsLink.size > 1) {
    errors.add(CONFLICTING_PARENT_LINE_OMS_LINKS_AD_SEQUENCE_ERROR);
  }

  const uniqueLineItemIds = new Set(ads.map((ad) => ad.lineItem?.id).filter(Boolean) as string[]);

  if (uniqueParentLineOmsLink.size === 0) {
    if (allowStandaloneLines) {
      if (uniqueLineItemIds.size > 1) {
        errors.add(CONFLICTING_PARENT_LINE_OMS_LINKS_AD_SEQUENCE_ERROR);
      }
    } else {
      errors.add(CONFLICTING_PARENT_LINE_OMS_LINKS_AD_SEQUENCE_ERROR);
    }
  }

  return {
    adQuantities,
    errors: Array.from(errors),
  };
};

const isAdSelectorType = (type: string): boolean =>
  type === adTypes.AD_SELECTOR_SLATE.key || type === adTypes.AD_SELECTOR_VIDEO.key;

const getEffectiveSlotPosition = (ad: SequenceAdV5): string | null => {
  if (!ad.adSequence) return null;
  return ad.adSequence.podAlignment ?? getDefaultSlotPosition(ad.lineItem?.countries);
};

export const getPodPositionIndex = (podPosition: string | null | undefined): number => {
  if (!podPosition) return -1;
  if (podPosition === 'PREROLL') return 0;
  if (podPosition === 'POSTROLL') return 22;
  const match = /MIDROLL_(\d+)/.exec(podPosition);
  return match ? parseInt(match[1], 10) : -1;
};

export const validatePodSlotRequired = (ads: SequenceAdV5[]): RowError[] => {
  const rowErrors: RowError[] = [];

  ads.forEach((ad) => {
    if (!ad.adSequence?.podPosition) {
      rowErrors.push({ adId: ad.id, message: POD_POSITION_UNSET_ERROR });
    }
    const effectiveSlot = getEffectiveSlotPosition(ad);
    if (!effectiveSlot) {
      rowErrors.push({ adId: ad.id, message: SLOT_POSITION_UNSET_ERROR });
    }
  });

  return rowErrors;
};

const validatePrerollRequirements = (
  ads: SequenceAdV5[],
  sequenceId: string,
  isAdSelector: boolean,
  errorsWithSequenceIds: Record<string, Set<string>>
): void => {
  const prerollAds = ads.filter((ad) => ad.adSequence?.podPosition === 'PREROLL');

  if (prerollAds.length === 0) return;

  if (isAdSelector) {
    const hasSlate = prerollAds.some((ad) => ad.type === adTypes.AD_SELECTOR_SLATE.key);
    const videoCount = prerollAds.filter((ad) => ad.type === adTypes.AD_SELECTOR_VIDEO.key).length;
    if (!hasSlate || videoCount < 1) {
      errorsWithSequenceIds[AD_SELECTOR_PREROLL_REQUIREMENTS_ERROR].add(sequenceId);
    }
  } else {
    const hasSlate = prerollAds.some((ad) => ad.type === adTypes.SLATE.key);
    const hasVideo = prerollAds.some((ad) => ad.type === adTypes.VIDEO.key);
    if (!hasSlate && !hasVideo) {
      errorsWithSequenceIds[PREROLL_REQUIREMENTS_ERROR].add(sequenceId);
    }
  }
};

const validateSequenceOrder = (
  ads: SequenceAdV5[],
  sequenceId: string,
  errorsWithSequenceIds: Record<string, Set<string>>
): void => {
  for (let i = 1; i < ads.length; i++) {
    const currentPodIndex = getPodPositionIndex(ads[i].adSequence?.podPosition);
    const previousPodIndex = getPodPositionIndex(ads[i - 1].adSequence?.podPosition);

    if (currentPodIndex !== -1 && previousPodIndex !== -1 && currentPodIndex < previousPodIndex) {
      errorsWithSequenceIds[REORDER_ACROSS_POD_ERROR].add(sequenceId);
      return;
    }
  }
};

const validateAdSelectorSlateOrder = (
  ads: SequenceAdV5[],
  sequenceId: string,
  errorsWithSequenceIds: Record<string, Set<string>>
): void => {
  const firstAdSelectorAd = ads.find((ad) => isAdSelectorType(ad.type));
  if (firstAdSelectorAd && firstAdSelectorAd.type === adTypes.AD_SELECTOR_VIDEO.key) {
    errorsWithSequenceIds[AD_SELECTOR_SLATE_ORDER_ERROR].add(sequenceId);
  }
};

const validateAdSelectorSlatePrerollStart = (
  ads: SequenceAdV5[],
  sequenceId: string,
  errorsWithSequenceIds: Record<string, Set<string>>
): void => {
  const slateAds = ads.filter((ad) => ad.type === adTypes.AD_SELECTOR_SLATE.key);

  slateAds.forEach((slate) => {
    const podPosition = slate.adSequence?.podPosition;
    const slotPosition = getEffectiveSlotPosition(slate);

    if (podPosition !== 'PREROLL' || slotPosition !== 'START') {
      errorsWithSequenceIds[AD_SELECTOR_SLATE_PREROLL_START_ERROR].add(sequenceId);
    }
  });
};

const validateAdSelectorPairing = (
  ads: SequenceAdV5[],
  sequenceId: string,
  errorsWithSequenceIds: Record<string, Set<string>>
): void => {
  const slateAds = ads.filter((ad) => ad.type === adTypes.AD_SELECTOR_SLATE.key);
  const videoAds = ads.filter((ad) => ad.type === adTypes.AD_SELECTOR_VIDEO.key);

  slateAds.forEach((slate) => {
    const slatePod = slate.adSequence?.podPosition;
    const slateSlot = getEffectiveSlotPosition(slate);

    videoAds.forEach((video) => {
      const videoPod = video.adSequence?.podPosition;
      const videoSlot = getEffectiveSlotPosition(video);

      if (slatePod && videoPod && slatePod === videoPod) {
        if (slateSlot !== videoSlot) {
          errorsWithSequenceIds[AD_SELECTOR_POD_SLOT_MISMATCH_ERROR].add(sequenceId);
        }
      }
    });
  });
};

const validateAdQuantityRules = (
  ads: SequenceAdV5[],
  sequenceId: string,
  errorsWithSequenceIds: Record<string, Set<string>>,
  allowStandaloneLines = false
): void => {
  const adSequenceValidationKey = getAdSequenceValidationKey(ads[0].type);
  if (!adSequenceValidationKey) return;

  const validationRule = getAdSequenceValidationRule(adSequenceValidationKey, ads, allowStandaloneLines);
  const { errors: adQuantitiesErrors, adQuantities } = calculateAdQuantities(validationRule, ads, {
    allowStandaloneLines,
  });

  if (adQuantitiesErrors.length) {
    adQuantitiesErrors.forEach((error) => errorsWithSequenceIds[error].add(sequenceId));
    return;
  }

  if (!validateAdsQuantities(validationRule, adQuantities)) {
    errorsWithSequenceIds[adSequenceValidationKey].add(sequenceId);
  }
};

const validateSequenceWithPodPosition = (
  ads: SequenceAdV5[],
  sequenceId: string,
  errorsWithSequenceIds: Record<string, Set<string>>,
  rowErrors: RowError[],
  allowStandaloneLines = false
): void => {
  const isAdSelector = ads.some((ad) => isAdSelectorType(ad.type));

  rowErrors.push(...validatePodSlotRequired(ads));
  validatePrerollRequirements(ads, sequenceId, isAdSelector, errorsWithSequenceIds);
  validateSequenceOrder(ads, sequenceId, errorsWithSequenceIds);

  validateAdQuantityRules(ads, sequenceId, errorsWithSequenceIds, allowStandaloneLines);

  if (isAdSelector) {
    validateAdSelectorSlatePrerollStart(ads, sequenceId, errorsWithSequenceIds);
    validateAdSelectorSlateOrder(ads, sequenceId, errorsWithSequenceIds);
    validateAdSelectorPairing(ads, sequenceId, errorsWithSequenceIds);
  }
};

export const validateSequences = (sequences: TransformedSequence[], allowStandaloneLines = false): ValidationResult => {
  const errorsWithSequenceIds: Record<string, Set<string>> = getDefaultErrorsState();
  const rowErrors: RowError[] = [];

  sequences.forEach(({ id, ads }) => {
    if (!ads.length) {
      errorsWithSequenceIds[EMPTY_AD_SEQUENCE_ERROR].add(id);
      return;
    }

    validateSequenceWithPodPosition(ads, id, errorsWithSequenceIds, rowErrors, allowStandaloneLines);
  });

  return { errorsWithSequenceIds, rowErrors };
};

export const canReorderAcrossPods = (ads: SequenceAdV5[], sourceIndex: number, destinationIndex: number): boolean => {
  const movedAd = ads[sourceIndex];
  if (!movedAd?.adSequence?.podPosition) return true;

  const newAds = [...ads];
  const [removed] = newAds.splice(sourceIndex, 1);
  newAds.splice(destinationIndex, 0, removed);

  for (let i = 1; i < newAds.length; i++) {
    const currentPodIndex = getPodPositionIndex(newAds[i].adSequence?.podPosition);
    const previousPodIndex = getPodPositionIndex(newAds[i - 1].adSequence?.podPosition);

    if (currentPodIndex !== -1 && previousPodIndex !== -1 && currentPodIndex < previousPodIndex) {
      return false;
    }
  }

  return true;
};
